import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../contexts/ToastContext";
import { updatePrivacySettingsAction, getPrivacySettingsAction } from "../../redux/actions/userActions";
import ButtonLoadingSpinner from "../loader/ButtonLoadingSpinner";

const PrivacySettings = () => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const user = useSelector((state) => state.auth?.userData);
  const privacySettings = useSelector((state) => state.user?.privacySettings);

  const [profileVisibility, setProfileVisibility] = useState("anyone");
  const [followPrivacy, setFollowPrivacy] = useState("anyone");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadPrivacySettings = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(getPrivacySettingsAction());
    } catch (error) {
      console.error("Error loading privacy settings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      loadPrivacySettings();
    }
  }, [user, loadPrivacySettings]);

  useEffect(() => {
    if (privacySettings) {
      setProfileVisibility(privacySettings.profileVisibility || "anyone");
      setFollowPrivacy(privacySettings.followPrivacy || "anyone");
    }
  }, [privacySettings]);

  const handleSave = async () => {
    setIsSaving(true);
    const startTime = Date.now();
    try {
      // Build settings object - only include relevant fields
      const settings = {
        profileVisibility,
        followPrivacy,
      };

      console.log("Saving privacy settings:", settings);
      const result = await dispatch(updatePrivacySettingsAction(settings));
      console.log("Save result:", result);
      
      if (result && result.success) {
        showSuccess("Privacy settings updated successfully");
      } else {
        const errorMsg = result?.error || "Failed to update privacy settings";
        console.error("Save error:", errorMsg);
        showError(errorMsg);
      }
    } catch (error) {
      console.error("Save exception:", error);
      showError(error.message || "An error occurred while updating privacy settings");
    } finally {
      // Ensure loader is visible for at least 600ms for better UX
      const elapsed = Date.now() - startTime;
      const MIN_DURATION = 600;
      if (elapsed < MIN_DURATION) {
        setTimeout(() => setIsSaving(false), MIN_DURATION - elapsed);
      } else {
        setIsSaving(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <ButtonLoadingSpinner loadingText="Loading privacy settings..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 font-roboto-flex">
        Privacy Settings
      </h2>

      {/* Profile Visibility */}
      <div className="mb-6">
        <label
          htmlFor="profile-visibility"
          className="block text-sm font-medium text-gray-700 mb-2 font-roboto-flex"
        >
          Profile Visibility
        </label>
        <select
          id="profile-visibility"
          className="w-48 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-roboto-flex"
          value={profileVisibility}
          onChange={(e) => {
            setProfileVisibility(e.target.value);
          }}
        >
          <option value="anyone">Anyone</option>
          <option value="private">Private</option>
        </select>
        <p className="mt-2 text-sm text-gray-500 font-roboto-flex">
          {profileVisibility === "anyone"
            ? "Your profile is visible to everyone"
            : "Your profile is private and requires permission to view"}
        </p>
      </div>

      {/* Who Can Follow Me */}
      <div className="mb-6">
        <label
          htmlFor="follow-privacy"
          className="block text-sm font-medium text-gray-700 mb-2 font-roboto-flex"
        >
          Who can follow me?
        </label>
        <select
          id="follow-privacy"
          className="w-48 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-roboto-flex"
          value={followPrivacy}
          onChange={(e) => {
            setFollowPrivacy(e.target.value);
          }}
        >
          <option value="anyone">Anyone</option>
          <option value="onlyPeopleIFollow">Only people I follow</option>
        </select>
        <p className="mt-2 text-sm text-gray-500 font-roboto-flex">
          {followPrivacy === "anyone"
            ? "Any user can follow your profile"
            : "Only users you already follow can follow you back"}
        </p>
        <p className="mt-1 text-xs text-gray-400 font-roboto-flex">
          {followPrivacy === "anyone"
            ? "Works for logged-in users. No approval required."
            : "Acts like a mutual-follow rule. Follow button will be disabled for others."}
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-6 py-2 rounded-md font-medium font-roboto-flex transition-colors ${
            isSaving
              ? "bg-gray-400 cursor-not-allowed text-white"
              : "bg-primary-700 text-white hover:bg-primary-800"
          }`}
        >
          {isSaving ? (
            <ButtonLoadingSpinner loadingText="Saving..." />
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </div>
  );
};

export default PrivacySettings;

